import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { MuiTelInput } from "mui-tel-input";

export default function ProfileSection({
  profile,
  updatedProfile,
  inputErrors,
  editMode,
  onEditClick,
  handleInputChange,
  handlePhoneChange,
}) {

  return (
    <>
      <Typography variant="h6" fontWeight="bold" color="#00438E">
        <FormattedMessage id="account.profile_info" />
      </Typography>
      <Divider sx={{ mb: 3, borderColor: "#00438E" }} />
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={3} sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={3}>
          <Avatar sx={{ width: 90, height: 90, border: "2px solid #00438E" }} />
          <Box>
            <Typography variant="h6" color="#00438E">
              {profile.first_name} {profile.last_name}
            </Typography>
            <Typography
              color="textSecondary"
              variant="body2"
              sx={{ mt: 1, maxWidth: "500px" }}
            >
              {profile.bio || "No bio provided"}
            </Typography>
          </Box>
        </Box>
        {!editMode && onEditClick && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            onClick={onEditClick}
            sx={{
              backgroundColor: "#00438E",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#003A75" }
            }}
          >
            <FormattedMessage id="account.edit_profile" defaultMessage="Edit Profile" />
          </Button>
        )}
      </Box>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          borderLeft: "4px solid #00438E",
          backgroundColor: "#ffffff",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={<FormattedMessage id="account.bio" />}
              name="bio"
              value={updatedProfile.bio}
              onChange={handleInputChange}
              disabled={!editMode}
              error={!!inputErrors.bio}
              helperText={inputErrors.bio}
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={<FormattedMessage id="account.username" />}
              name="username"
              value={profile.username}
              disabled
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={<FormattedMessage id="account.email" />}
              name="email"
              value={profile.email}
              disabled
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={<FormattedMessage id="account.first_name" />}
              name="first_name"
              value={updatedProfile.first_name}
              onChange={handleInputChange}
              disabled={!editMode}
              error={!!inputErrors.first_name}
              helperText={inputErrors.first_name}
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={<FormattedMessage id="account.last_name" />}
              name="last_name"
              value={updatedProfile.last_name}
              onChange={handleInputChange}
              disabled={!editMode}
              error={!!inputErrors.last_name}
              helperText={inputErrors.last_name}
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={<FormattedMessage id="account.org_name" />}
              name="organization_name"
              value={updatedProfile.organization_name}
              onChange={handleInputChange}
              disabled={!editMode}
              error={!!inputErrors.organization_name}
              helperText={inputErrors.organization_name}
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput
              fullWidth
              label={<FormattedMessage id="account.phone_number" />}
              name="phone_number"
              value={updatedProfile.phone_number}
              onChange={handlePhoneChange}
              disabled={!editMode}
              defaultCountry="CA"
              preferredCountries={["CA", "US", "GB"]}
              continents={["NA", "EU"]}
              error={!!inputErrors.phone_number}
              helperText={inputErrors.phone_number}
              sx={{
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}